/**
 * Sentry initialization for the React frontend.
 *
 * Guarded by `REACT_APP_SENTRY_DSN` — when the DSN is missing (dev, CI,
 * preview builds without secrets), Sentry is not initialized and the app runs
 * exactly as before.
 *
 * Import this module BEFORE rendering the app (see src/index.js).
 */
import * as Sentry from '@sentry/react';

const dsn = (process.env.REACT_APP_SENTRY_DSN || '').trim();

function fraction(name, fallback) {
  const v = Number(process.env[name]);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;
}

if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
    sampleRate: fraction('REACT_APP_SENTRY_SAMPLE_RATE', 1.0),
    tracesSampleRate: fraction('REACT_APP_SENTRY_TRACES_SAMPLE_RATE', 0.1),
    integrations: [
      Sentry.browserTracingIntegration({
        instrumentPageLoad: true,
        instrumentNavigation: true,
      }),
    ],
    // Distributed tracing: only send trace headers to our own API origin.
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/[^/]*dowurktoday\.(org|com)\//,
      new RegExp(`^${(process.env.REACT_APP_BACKEND_URL || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`),
    ],
    // Privacy: strip cookies + body payloads before send. Emails scrubbed too.
    beforeSend(event) {
      if (event.user) delete event.user.email;
      if (event.request) {
        delete event.request.cookies;
        delete event.request.data;
      }
      return event;
    },
  });
  // Expose a minimal test helper for verifying wiring from DevTools:
  //   window.__dowurkSentryTest('message' | 'exception')
  if (typeof window !== 'undefined') {
    window.__dowurkSentryTest = (kind = 'message') => {
      if (kind === 'exception') {
        try {
          throw new Error('DowUrk Sentry FE test exception — safe to ignore');
        } catch (e) {
          return Sentry.captureException(e);
        }
      }
      return Sentry.captureMessage('DowUrk Sentry FE test message — safe to ignore');
    };
  }
}

export { Sentry };
